import { useContext, useEffect, useState } from "react";
import { TransferContext } from "../../context/TransferContext";
import {
  DestinationStep,
  STEP_DESTINATION_CONNECT_ES,
  STEP_DESTINATION_CONNECT_IRA_TYPE,
  STEP_DESTINATION_HELP,
  STEP_DESTINATION_HELP_1,
  STEP_DESTINATION_HELP_2,
  STEP_DESTINATION_HELP_3,
  STEP_DESTINATION_HELP_4,
  STEP_DESTINATION_HELP_5,
  STEP_DESTINATION_HELP_6A,
  STEP_DESTINATION_HELP_6B,
  STEP_DESTINATION_HELP_6C,
  STEP_DESTINATION_HELP_RESULT,
  STEP_DESTINATION_HELP_RESULT_LOADING,
} from "../../class/transferDetails";
import { TransferOptionValues } from "../../utils/getTransferOptionContent";
import { BodyLargeRegular, TitleLarge } from "../../components/font";
import { Button } from "@manifesthhqinc/ui-library";
import { ModalContainerContentHeader, ModalContainerCTAButtonDiv } from "../general/modal/styles";

export type UseHenryQuestionniareProps = ReturnType<typeof useHenryQuestionniare>;
export enum RetirementHorizon {
  MORE_THAN_20_YEARS = "MORE_THAN_20_YEARS",
  BETWEEN_10_AND_20_YEARS = "BETWEEN_10_AND_20_YEARS",
  LESS_THAN_OR_EQUAL_10_YEARS = "LESS_THAN_OR_EQUAL_10_YEARS",
}
export enum InvestmentPreference {
  SELF_DIRECTED = "SELF_DIRECTED",
  GUIDED_OR_AUTOMATED = "GUIDED_OR_AUTOMATED",
}
export enum InvestmentConfidence {
  COMFORTABLE = "COMFORTABLE",
  NOT_CONFIDENT = "NOT_CONFIDENT",
  MODERATE = "MODERATE",
}

export enum FinancialDecisionValue {
  PEACE_OF_MIND = "PEACE_OF_MIND",
  CONTROL = "CONTROL",
  SIMPLICITY = "SIMPLICITY",
}

export enum FeeAcceptanceForManagement {
  VALUE_HELP = "VALUE_HELP",
  UNSURE = "UNSURE",
  AVOID_FEES = "AVOID_FEES",
}

export enum InvestmentOptionsPreference {
  FULL_CONTROL = "FULL_CONTROL",
  SMALLER_LIST_OK = "SMALLER_LIST_OK",
  UNSURE = "UNSURE",
}

export type HenryQuestionnaireData = {
  retirementHorizon?: RetirementHorizon;
  hasCurrentEmployerSponsoredAccount?: boolean;
  investmentPreference?: InvestmentPreference;
  investmentConfidence?: InvestmentConfidence;
  loanImportant?: boolean;
  financialDecisionValue?: FinancialDecisionValue;
  feeAcceptanceForManagement?: FeeAcceptanceForManagement;
  investmentOptionsPreference?: InvestmentOptionsPreference;
};

const henryScreenName: DestinationStep[] = [
  STEP_DESTINATION_HELP,
  STEP_DESTINATION_HELP_1,
  STEP_DESTINATION_HELP_2,
  STEP_DESTINATION_HELP_3,
  STEP_DESTINATION_HELP_4,
  STEP_DESTINATION_HELP_5,
];

const useHenryQuestionniare = () => {
  const {
    goToScreen,
    setIsLoading,
    currentScreen,
    userDetails,
    transferOptions,
    transferOptionContent,
    sendHenryQuestionnaireData,
    setUserSelectedTransferOption,
    setShowModal,
    setModalContent,
  } = useContext(TransferContext);

  const [henryQuestionnareData, setHenryQuestionnaireData] = useState<HenryQuestionnaireData>({});
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [selectedTransferOption, setSelectedTransferOption] = useState<TransferOptionValues>(
    TransferOptionValues.noOption,
  );

  const onClickStart = () => {
    setCurrentStep(1);
    goToScreen(STEP_DESTINATION_HELP_1);
  };

  const onConfirmSelectOption = () => {
    setUserSelectedTransferOption(selectedTransferOption);
    if (selectedTransferOption === TransferOptionValues.employerSponsored) {
      goToScreen(STEP_DESTINATION_CONNECT_ES);
    } else if (
      selectedTransferOption === TransferOptionValues.ira ||
      selectedTransferOption === TransferOptionValues.managedIRA
    ) {
      goToScreen(STEP_DESTINATION_CONNECT_IRA_TYPE);
    }
  };

  const onClickRestart = () => {
    setShowModal(true);
    setModalContent(
      <>
        <ModalContainerContentHeader>
          <TitleLarge>Restart these questions?</TitleLarge>
          <BodyLargeRegular>Answer Henry’s questions again for an updated recommendation.</BodyLargeRegular>
        </ModalContainerContentHeader>
        <ModalContainerCTAButtonDiv>
          <Button onClick={confirmRestart} variant="secondary">
            Yes
          </Button>
          <Button onClick={() => setShowModal(false)}>No</Button>
        </ModalContainerCTAButtonDiv>
      </>,
    );
  };

  const confirmRestart = () => {
    setShowModal(false);
    setHenryQuestionnaireData({});
    setCurrentStep(0);
    goToScreen(STEP_DESTINATION_HELP);
  };

  const sendDataToAPI = (henryData: Partial<HenryQuestionnaireData>) => {
    sendHenryQuestionnaireData(henryData);
  };

  const updateHenryQuestionnaire = async (data: Partial<HenryQuestionnaireData>) => {
    const updatedData = { ...henryQuestionnareData, ...data };
    setHenryQuestionnaireData(updatedData);

    if (currentStep === 6) {
      goToScreen(STEP_DESTINATION_HELP_RESULT_LOADING);
      sendDataToAPI(updatedData);
    } else if (currentStep === 5) {
      if (
        (henryQuestionnareData.investmentPreference === InvestmentPreference.SELF_DIRECTED &&
          henryQuestionnareData.investmentConfidence !== InvestmentConfidence.COMFORTABLE) ||
        (henryQuestionnareData.investmentPreference === InvestmentPreference.GUIDED_OR_AUTOMATED &&
          henryQuestionnareData.investmentConfidence === InvestmentConfidence.COMFORTABLE)
      ) {
        setCurrentStep(6);
        goToScreen(STEP_DESTINATION_HELP_6A);
      } else if (
        (henryQuestionnareData.investmentPreference === InvestmentPreference.GUIDED_OR_AUTOMATED ||
          henryQuestionnareData.investmentConfidence !== InvestmentConfidence.COMFORTABLE) &&
        (henryQuestionnareData.hasCurrentEmployerSponsoredAccount === false ||
          henryQuestionnareData.loanImportant === false)
      ) {
        setCurrentStep(6);
        goToScreen(STEP_DESTINATION_HELP_6B);
      } else if (
        henryQuestionnareData.investmentPreference === InvestmentPreference.SELF_DIRECTED &&
        henryQuestionnareData.investmentConfidence === InvestmentConfidence.COMFORTABLE &&
        henryQuestionnareData.hasCurrentEmployerSponsoredAccount
      ) {
        setCurrentStep(6);
        goToScreen(STEP_DESTINATION_HELP_6C);
      } else {
        goToScreen(STEP_DESTINATION_HELP_RESULT_LOADING);
        sendDataToAPI(updatedData);
      }
    } else if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      goToScreen(henryScreenName[currentStep + 1]);
    }
  };

  const showResult = () => {
    setIsLoading(false);
    goToScreen(STEP_DESTINATION_HELP_RESULT);
  };

  useEffect(() => {
    if (transferOptions?.FIRST) {
      setSelectedTransferOption(transferOptions.FIRST);
    }
  }, [transferOptions?.FIRST]);

  return {
    currentStep,
    onConfirmSelectOption,
    userDetails,
    currentScreen,
    updateHenryQuestionnaire,
    setIsLoading,
    showResult,
    transferOptions,
    transferOptionContent,
    selectedTransferOption,
    setSelectedTransferOption,
    onClickStart,
    onClickRestart,
    goToScreen,
  };
};

export default useHenryQuestionniare;
